import type { StateCreator } from 'zustand';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import isEqual from 'lodash/isEqual';
import type {
  EventBrandValuesSet,
  NonGeneratedEventBrandValues,
  SavedEventBrandValues,
} from './types';
import { getPersistedBrandCustomizations } from '../utilities/utilities';
import { getGeneratedValuesForBrand } from '../utilities/getGeneratedValuesForBrand';

export type BrandStore = {
  deleteLightLogo: () => void;
  deleteDarkLogo: () => void;

  hashedId: string | undefined;
  isDataLoaded: boolean;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isSavingEventAndForm: boolean;
  initializeBrand: (hashedId: string | undefined, brand: NonGeneratedEventBrandValues) => void;
  brand: EventBrandValuesSet;
  savedBrand: SavedEventBrandValues;
  updateBrand: (updatedBrand: Partial<NonGeneratedEventBrandValues>) => void;
  discardBrandChanges: () => void;
  hasOutstandingBrandChanges: () => boolean;
  brandChanges: () => string[];
};

export const brandStoreCreator: StateCreator<BrandStore> = (set, get) => ({
  hashedId: undefined,
  deleteLightLogo: () =>
    set((state) => {
      const newBrand = { ...state.brand };
      newBrand.lightLogoUrl = '';
      newBrand.lightLogoBakeryHashedId = '';
      return {
        brand: newBrand,
      };
    }),
  deleteDarkLogo: () =>
    set((state) => {
      const newBrand = { ...state.brand };
      newBrand.darkLogoUrl = '';
      newBrand.darkLogoBakeryHashedId = '';
      return {
        brand: newBrand,
      };
    }),
  isDataLoaded: false,
  isLoading: true,
  setIsLoading: (isLoading: boolean) => set(() => ({ isLoading })),
  isSavingEventAndForm: false,
  brand: {} as EventBrandValuesSet,
  savedBrand: {} as SavedEventBrandValues,
  updateBrand: (updatedBrand: Partial<NonGeneratedEventBrandValues>) => {
    set((state) => {
      const brandKeyValues = Object.keys(state.brand);
      const newBrand = brandKeyValues.reduce(
        (acc: Partial<NonGeneratedEventBrandValues>, brandKey: keyof EventBrandValuesSet) => {
          const incomingBrandValue = updatedBrand[brandKey as keyof typeof updatedBrand];
          if (brandKey in updatedBrand) {
            acc[brandKey] = incomingBrandValue;
          }
          return acc;
        },
        state.brand,
      ) as NonGeneratedEventBrandValues;

      const brandWithGeneratedValues = getGeneratedValuesForBrand(newBrand);
      return {
        brand: brandWithGeneratedValues,
      };
    });
  },
  discardBrandChanges: () => {
    set((state) => {
      const brand = getGeneratedValuesForBrand({
        ...state.savedBrand,
        darkLogoUrl: state.brand.darkLogoUrl,
        lightLogoUrl: state.brand.lightLogoUrl,
      } as EventBrandValuesSet);

      return { brand };
    });
  },
  brandChanges: () => {
    const { brand, savedBrand } = get();
    const pendingChanges = getPersistedBrandCustomizations(brand);
    const savedChanges = savedBrand;

    return Object.keys(pendingChanges).reduce((acc: string[], key) => {
      const isChanged = !isEqual(pendingChanges[key], savedChanges[key]);
      if (isChanged) {
        acc.push(key);
      }
      return acc;
    }, []);
  },
  hasOutstandingBrandChanges: () => {
    const { brand, savedBrand } = get();
    const persistedCustomizations = getPersistedBrandCustomizations(brand);
    return !isEqual(persistedCustomizations, savedBrand);
  },
  initializeBrand: (hashedId, brand) => {
    set((state) => {
      // Changes in the Brand tab refetch the '~/singlePageApp/screens/LiveStreamScreen/LiveStreamOverview/OverviewPage/gql/__gql/LiveStreamOverviewQueries.gql.gen'Document query, which is bound
      // in an effect on the LiveStreamCustomizeScreen that re-initializes the brand.
      // If multiple brand updates are enqueued (e.g., quickly changing colors on a poor connection),
      // the saved data from the backend can overwrite the previewed/pending save data.
      // To prevent this, we'll allow whatever exists for the brand in state to override what's
      // fetched from the backend.
      const brandWithGeneratedValues = getGeneratedValuesForBrand({
        ...brand,
        ...(hashedId === state.hashedId ? state.brand : {}),
        hasPoweredByWistiaBadge: brand.hasPoweredByWistiaBadge, // We should ALWAYS let the newest data determine if the badge should be shown
      });

      return {
        brand: brandWithGeneratedValues,
        savedBrand: getPersistedBrandCustomizations(brandWithGeneratedValues),
        isLoading: false,
        isDataLoaded: true,
        hashedId,
      };
    });
  },
});

export const useBrandStore = create<BrandStore>()(subscribeWithSelector(brandStoreCreator));
